import React, { useState, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import ScrollReveal from "./ScrollReveal";
import Footer from "./Footer";
import { ArrowLeft } from "lucide-react";

const SaseTransformation = () => {
  const navigate = useNavigate();
  // Reference for the timeline section to track scroll progress
  const timelineRef = useRef(null);

  // Captures scroll progress specifically within this container
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start center", "end center"],
  });

  // Transforms the scroll progress into a height scale percentage (from 0% to 100%)
  const scaleY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const tabsData = [
    {
      id: "tab1",
      title: "Business-Driven Transformation",
      contentTitle: "Business-Driven Transformation",
      description:
        "We align technology initiatives with business outcomes, operational goals, and security requirements.",
    },
    {
      id: "tab2",
      title: "Strategic Roadmaps & Operating Models",
      contentTitle: "Strategic Roadmaps & Operating Models",
      description:
        "Define a future-ready target operating model, governance structure, and phased roadmap tailored to your organization’s maturity and objectives.",
    },
    {
      id: "tab3",
      title: "Vendor-Neutral Guidance",
      contentTitle: "Vendor-Neutral Guidance",
      description:
        "Receive expert advice and architectural guidance to make informed technology decisions across SASE, SSE, SD-WAN, Zero Trust, and cloud security platforms.",
    },
    {
      id: "tab4",
      title: "Accelerated Legacy Modernization",
      contentTitle: "Accelerated Legacy Modernization",
      description:
        "Simplify your transition to cloud-native networking and security with a seamless, low-risk transformation approach.",
    },
    {
      id: "tab5",
      title: "Enhanced Visibility & Control",
      contentTitle: "Enhanced Visibility & Control",
      description:
        "Gain centralized visibility, operational oversight, and lifecycle management capabilities.",
    },
  ];

  const [activeTab, setActiveTab] = useState("tab1");

  const timelineData = [
    {
      title: "Business & Stakeholder Alignment",
      description:
        "Align business, networking, security, and compliance stakeholders on transformation goals, security objectives, user experience expectations, and governance models to establish clear ownership, priorities, and success criteria for the SASE journey.",
    },
    {
      title: "Current State Assessment",
      description:
        "Conduct a comprehensive assessment of the current network, security, remote access, branch, cloud, and operational environment to identify architectural gaps, legacy dependencies, policy inconsistencies, operational inefficiencies, and transformation risks.",
    },
    {
      title: "Target Architecture & Operating Model Design",
      description:
        "Define a future-state SASE architecture aligned to Zero Trust, cloud-first connectivity, scalability, and operational resilience, including the target operating model, governance framework, policy structure, service ownership, and support model for long-term operations.",
    },
    {
      title: "Vendor & Technology Strategy",
      description:
        "Provide independent advisory and architectural guidance to evaluate and select SASE, SSE, SD-WAN, ZTNA, CASB, SWG, and cloud security solutions aligned to technical requirements, operational maturity, integration needs, and long-term business strategy.",
    },
    {
      title: "Transformation Roadmap",
      description:
        "Develop a phased migration strategy that modernizes connectivity and security with minimal disruption, including dependency mapping, coexistence planning, rollback procedures, tooling requirements, and transformation milestones for controlled execution.",
    },
    {
      title: "Implementation & Policy Transformation",
      description:
        "Deploy and configure SASE capabilities using standardized delivery frameworks tailored to the environment, modernizing access policies, identity controls, traffic steering, segmentation, cloud integrations, and remote access workflows while maintaining operational continuity and governance.",
    },
    {
      title: "Visibility, Optimization & Operationalization",
      description:
        "Enable centralized visibility, monitoring, reporting, and lifecycle management through the Synthera Security SASE Management Portal, while continuously optimizing policies, user experience, operational efficiency, security posture, scalability, and governance.",
    },
  ];

  return (
    <div className="bg-[#011916] text-white min-h-screen font-sans overflow-x-hidden selection:bg-[#048649] selection:text-white">
      <button
        onClick={() => navigate(-1)}
        className="absolute top-20 left-8 z-50 flex items-center justify-center w-10 h-10 rounded-full bg-black/20 hover:bg-black/40 text-white/70 hover:text-[#24ff81] transition-all duration-300"
      >
        <ArrowLeft size={20} />
      </button>
      {/* SECTION 1: TOP HERO HEADER SECTION */}
      <ScrollReveal>
        <div className="pt-32 pb-16 px-6 max-w-[1600px] mx-auto">
          <section className="relative bg-[#022722] rounded-[3rem] p-8 md:p-10 lg:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            {/* LEFT CONTENT */}
            <div className="md:col-span-6">
              <h1 className="text-2xl md:text-4xl font-black text-white leading-tight mb-4 tracking-tight">
                SASE Transformation
              </h1>

              <h4 className="text-white text-lg md:text-xl font-extrabold mb-5 tracking-wide uppercase">
                Modernising Network and Security for the Cloud-First, Hybrid
                Workforce
              </h4>

              <p className="text-gray-300 text-base md:text-lg leading-[1.8] mb-10 max-w-2xl">
                Legacy network and security architectures are struggling to
                support today’s cloud-first, hybrid, and AI-driven environments.
                Synthera Security’s SASE Transformation Service helps
                organizations modernize connectivity and security through a
                strategic, outcome-driven approach built on SASE, Zero Trust,
                and cloud-native architectures.
                <br />
                <br />
                From strategy and roadmap development to migration and
                operationalization, we help enterprises simplify operations,
                strengthen security, and transition from fragmented legacy
                environments to scalable, resilient, and cloud-delivered
                security frameworks.
              </p>

              <Link
                to="/contact"
                className="bg-[#048649] hover:bg-[#036c3a] text-white font-bold text-sm md:text-base px-7 py-3 rounded-md transition-all duration-200 w-fit no-underline shadow-lg shadow-green-950/20"
              >
                Work with Us
              </Link>
            </div>

            {/* RIGHT IMAGE */}
            <div className="md:col-span-6 flex justify-center items-center w-full">
              <div className="w-[85%] rounded-2xl overflow-hidden shadow-2xl shadow-green-950/10">
                <img
                  src="https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1780214428/sase_T_n01yp9.jpg"
                  alt="SASE Transformation Visual"
                  className="w-full h-[600px] object-cover rounded-2xl"
                />
              </div>
            </div>
          </section>
        </div>
      </ScrollReveal>

      {/* SECTION 2: EXPERIENCE THE DIFFERENCE (TAB SELECTOR) */}
      <ScrollReveal>
        <section className="relative bg-[#011916] py-20 px-6 max-w-6xl mx-auto">
          <div className="text-center md:text-left mb-12">
            <span className="text-gray-300 text-xs font-bold uppercase tracking-wider block mb-1">
              Why Choose Synthera Security?
            </span>
            <h2 className="text-lg md:text-2xl font-black text-white uppercase tracking-tight">
              Experience the Difference
            </h2>
            <p className="text-gray-300 text-xs md:text-sm mt-2">
              Synthera Security combines deep cybersecurity expertise with
              practical transformation experience to help enterprises
              successfully adopt modern SASE architectures.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            {/* Left Button Controls Menu */}
            <div className="md:col-span-5 flex flex-col gap-2">
              {tabsData.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left px-5 py-3.5 rounded-lg border text-xs md:text-sm font-bold transition-all duration-200 ${
                    activeTab === tab.id
                      ? "bg-[#04433c] text-emerald-300 border-emerald-500/30 shadow-md"
                      : "bg-[#021f1b] text-gray-400 border-white/5 hover:bg-[#022722] hover:text-white"
                  }`}
                >
                  {tab.title}
                </button>
              ))}
            </div>

            {/* Right Display Presentation Panel */}
            <div className="md:col-span-7 h-full min-h-[200px]">
              <div className="bg-[#021f1b] border border-white/5 rounded-xl p-8 h-full flex flex-col justify-center relative min-h-[220px]">
                <AnimatePresence mode="wait">
                  {tabsData.map(
                    (tab) =>
                      tab.id === activeTab && (
                        <motion.div
                          key={tab.id}
                          initial={{ opacity: 0, x: 8 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -8 }}
                          transition={{ duration: 0.15 }}
                        >
                          <h3 className="text-lg md:text-xl font-bold text-white mb-3">
                            {tab.contentTitle}
                          </h3>
                          <p className="text-gray-300 text-xs md:text-sm leading-relaxed">
                            {tab.description}
                          </p>
                        </motion.div>
                      ),
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* SECTION 3: MID-PAGE EXPOSITION */}
      <ScrollReveal>
        <section className="bg-[#011916] py-16 px-6 text-center border-t border-white/5">
          <div className="max-w-3xl mx-auto">
            <span className="text-gray-300 text-xs font-semibold uppercase tracking-wider block mb-1">
              Our Approach
            </span>
            <h2 className="text-lg md:text-2xl font-black text-white tracking-tight uppercase mb-4">
              Beyond Traditional Network Modernization
            </h2>
            <p className="text-gray-300 text-xs md:text-sm leading-relaxed">
              Successful SASE transformation requires more than replacing legacy
              firewalls or deploying cloud security platforms. Modern
              enterprises operate across distributed users, SaaS applications,
              cloud workloads, and hybrid environments, requiring a strategic
              approach to connectivity and security. Synthera Security delivers
              a business-aligned transformation framework spanning architecture,
              operating models, vendor selection, migration planning, and
              operational visibility to build scalable, resilient, cloud-native
              security environments with minimal disruption.
            </p>
          </div>
        </section>
      </ScrollReveal>

      {/* SECTION 4: DYNAMIC TIMELINE / METHODOLOGY */}
      <ScrollReveal>
        <section
          ref={timelineRef}
          className="bg-[#031513] pb-32 pt-6 px-6 max-w-4xl mx-auto relative rounded-2xl"
        >
          <div className="mb-16 text-center md:text-left">
            <span className="text-gray-300 text-xs font-semibold uppercase tracking-wider block mb-1">
              Synthera Methodology
            </span>
            <h2 className="text-lg md:text-2xl font-black text-white uppercase tracking-tight">
              Transformation Process
            </h2>
          </div>

          {/* Timeline Wrapper Grid Layout */}
          <div className="relative pl-6 md:pl-10">
            {/* Background Base Muted Track Line */}
            <div className="absolute left-1.5 md:left-3 top-2 bottom-2 w-[1px] bg-[#021f1b]" />

            {/* DYNAMIC SCROLLING GREEN ACCENT LINE STEM */}
            <motion.div
              style={{ height: scaleY }}
              className="absolute left-1.5 md:left-3 top-2 w-[1px] bg-emerald-500 origin-top z-10 shadow-[0_0_8px_rgba(16,185,129,0.5)]"
            />

            <div className="flex flex-col gap-12 relative">
              {timelineData.map((item, index) => (
                <div key={index} className="w-full relative group">
                  {/* Dynamic Glowing Green Circle Node Marker */}
                  <motion.div
                    initial={{ scale: 0.6, backgroundColor: "#021f1b" }}
                    whileInView={{ scale: 1, backgroundColor: "#048649" }}
                    viewport={{ once: false, amount: "even" }}
                    transition={{ duration: 0.3 }}
                    className="absolute -left-[23px] md:-left-[35px] w-2.5 h-2.5 top-1.5 rounded-full z-20 ring-4 ring-[#032d28]"
                  />

                  {/* Dynamic Text Group revealing on Scroll viewport arrival */}
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.2 }}
                    transition={{ duration: 0.4 }}
                  >
                    <h3 className="text-base md:text-lg font-bold text-white mb-2 tracking-tight transition-colors duration-300 group-hover:text-emerald-400">
                      {item.title}
                    </h3>
                    <p className="text-gray-300 text-xs md:text-sm leading-relaxed max-w-3xl font-light">
                      {item.description}
                    </p>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>
      <Footer />
    </div>
  );
};

export default SaseTransformation;
